import * as PIXI from "pixi.js";

const tmpMatrix = new PIXI.Matrix();

/**
 * Pixi v8 camera.
 *
 * The v6 version kept the level container outside the scene graph and rendered
 * it manually with a hijacked ProjectionSystem transform. v8 has neither
 * DisplayObject.render(renderer) hooks nor a ProjectionSystem, so the camera is
 * now a plain Container: the level container becomes its child and the camera's
 * own x/y/scale (driven by the existing game/editor code) provides the view
 * transform. The public API (matrix, invertedMatrix, toScreenPoint) is intact.
 */
export class Camera extends PIXI.Container {
  /**
   * @param {PIXI.Container} stage the level container this camera shows
   * @param {PIXI.Container} [listen] container whose worldTransform defines the view
   */
  constructor(stage, listen) {
    // Move the world with one GPU transform. Without a render group, every
    // camera pan repacks the vertices of otherwise unchanged world objects.
    super({isRenderGroup: true});

    this.stage = stage;
    this.listen = listen || this;
    this.cameraMatrix = new PIXI.Matrix();
    this.pixelSnapping = true;

    this.addChild(stage);

    this.onRender = () => {
      if (this.pixelSnapping) {
        this.position.x = Math.round(this.position.x);
        this.position.y = Math.round(this.position.y);
      }
    };
  }

  forceUpdate() {}

  get matrix() {
    // A render group's worldTransform is cached until rendering. Input,
    // effects, and culling also need the current camera before that step.
    return this.listen.getGlobalTransform(this.cameraMatrix);
  }

  /**
   * @type {PIXI.Matrix}
   */
  get invertedMatrix() {
    tmpMatrix.copyFrom(this.matrix);
    tmpMatrix.invert();
    return tmpMatrix;
  }

  /**
   * Transform camera-local pixels to global screen pixels. Do not pass the
   * result of a descendant's toGlobal(): it already includes this camera.
   * For a HUD/overlay destination, use destination.toLocal(point, source).
   * @param {{x: number, y: number}} point
   * @param {{x: number, y: number}} [output]
   */
  toScreenPoint(point, output = new PIXI.Point()) {
    const m = this.matrix;

    m.apply(point, output);
    return output;
  }
}
